"use client"
import { cn } from "@/utils/helpers/utils"
import { generatePageNumbers, PageItem } from "@/utils/helpers/pagination"
import React from "react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"
import { useRouter } from "next/navigation"

interface PaginationProps {
   currentPage: number
   totalPages: number
   baseUrl?: string
   onPageChange?: (page: number) => void
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, baseUrl = "", onPageChange }) => {
   const router = useRouter()

   const goToPage = (page: number) => {
      if (onPageChange) {
         onPageChange(page)
      } else {
         router.push(`${baseUrl}?page=${page}`)
      }
   }

   // Generate page numbers for mobile (5 max) and desktop (7 max)
   const mobilePages = generatePageNumbers({ currentPage, totalPages, maxVisible: 5 })
   const desktopPages = generatePageNumbers({ currentPage, totalPages, maxVisible: 7 })

   // Helper function to render page items (numbers and ellipsis)
   const renderPageItems = (pages: PageItem[]) => {
      return pages.map((item, index) => {
         // Render ellipsis
         if (item === "ellipsis-start" || item === "ellipsis-end") {
            return (
               <span
                  key={`ellipsis-${item}-${index}`}
                  className="relative inline-flex cursor-default items-center bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                  aria-hidden="true"
               >
                  ...
               </span>
            )
         }

         // Render page number button
         return (
            <button
               key={item}
               onClick={() => goToPage(item)}
               aria-label={`Go to page ${item}`}
               aria-current={currentPage === item ? "page" : undefined}
               className={cn(
                  "relative inline-flex items-center px-4 py-2 text-sm font-semibold text-title ring-1 ring-inset ring-gray-300 hover:brightness-110 focus:z-20 focus:outline-offset-0",
                  currentPage === item && "z-10 bg-custom-primary text-white focus-visible:outline-custom-primary",
               )}
            >
               {item}
            </button>
         )
      })
   }

   return (
      <>
         {/* Mobile pagination (5 pages max) */}
         <nav className="mx-auto flex -space-x-px rounded-md shadow-sm sm:hidden" aria-label="Pagination">
            <button
               disabled={currentPage === 1}
               onClick={() => {
                  if (currentPage > 1) {
                     goToPage(currentPage - 1)
                  }
               }}
               className="relative inline-flex items-center rounded-l-md bg-[#EBEFF6] px-2 py-2 text-[#4E5D78] ring-1 ring-inset ring-gray-300 transition-all hover:bg-white hover:text-custom-primary hover:brightness-90 focus:z-20 focus:outline-offset-0 disabled:pointer-events-none disabled:opacity-50"
               aria-label="Previous page"
            >
               <span className="sr-only">Previous</span>
               <FaChevronLeft className="h-3 w-3 text-[#4E5D78]" aria-hidden="true" />
            </button>
            {renderPageItems(mobilePages)}
            <button
               disabled={currentPage === totalPages}
               onClick={() => {
                  if (currentPage < totalPages) {
                     goToPage(currentPage + 1)
                  }
               }}
               className="relative inline-flex items-center rounded-r-md bg-[#EBEFF6] px-2 py-2 text-[#4E5D78] ring-1 ring-inset ring-gray-300 transition-all hover:bg-white hover:text-custom-primary hover:brightness-90 focus:z-20 focus:outline-offset-0 disabled:hidden"
               aria-label="Next page"
            >
               <span className="sr-only">Next</span>
               <FaChevronRight className="h-3 w-3 text-[#4E5D78]" aria-hidden="true" />
            </button>
         </nav>

         {/* Desktop pagination (7 pages max) */}
         <nav className="mx-auto hidden -space-x-px rounded-md shadow-sm sm:flex" aria-label="Pagination">
            <button
               disabled={currentPage === 1}
               onClick={() => {
                  if (currentPage > 1) {
                     goToPage(currentPage - 1)
                  }
               }}
               className="relative inline-flex items-center rounded-l-md bg-[#EBEFF6] px-2 py-2 text-[#4E5D78] ring-1 ring-inset ring-gray-300 transition-all hover:bg-white hover:text-custom-primary hover:brightness-90 focus:z-20 focus:outline-offset-0 disabled:pointer-events-none disabled:opacity-50"
               aria-label="Previous page"
            >
               <span className="sr-only">Previous</span>
               <FaChevronLeft className="h-3 w-3 text-brand-black" aria-hidden="true" />
            </button>
            {renderPageItems(desktopPages)}
            <button
               disabled={currentPage === totalPages}
               onClick={() => {
                  if (currentPage < totalPages) {
                     goToPage(currentPage + 1)
                  }
               }}
               className="relative inline-flex items-center rounded-r-md bg-[#EBEFF6] px-2 py-2 text-[#4E5D78] ring-1 ring-inset ring-gray-300 transition-all hover:bg-white hover:text-custom-primary hover:brightness-90 focus:z-20 focus:outline-offset-0 disabled:hidden"
               aria-label="Next page"
            >
               <span className="sr-only">Next</span>
               <FaChevronRight className="h-3 w-3" aria-hidden="true" />
            </button>
         </nav>
      </>
   )
}

export default Pagination
